import { useLayoutEffect, useRef } from "react";

import { useGlobalsContext, useResizeObserver } from "shared/hooks";
import classNames from "shared/utils/classNames";
import { ReactComponent as IconClose } from "assets/images/icons/close-black.svg";

import StarterQuestionButton from "./StarterQuestionButton";
import { Props } from "./types";

function PopupMessagesNonIframe(props: Props) {
  const { widgetExpanded, setWidgetExpanded, parentPostMessage } =
    useGlobalsContext();

  const {
    botInfo,
    starterQuestions,
    popupClosedByUser,
    widgetPosition,
    onSuggestionClick,
    setPopupClosedByUser,
    chatDataLength,
    isOverButton,
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);

  const contentRect = useResizeObserver(rootRef);

  const hasSomethingToShow =
    botInfo.bot_info_config.show_floating_welcome_message ||
    (botInfo.bot_info_config.show_starter_widget && !!starterQuestions);

  const hidePopupMessages =
    popupClosedByUser ||
    (widgetExpanded && isOverButton) ||
    (isOverButton && !hasSomethingToShow) ||
    !!chatDataLength;

  useLayoutEffect(() => {
    if (widgetExpanded || !isOverButton) {
      return;
    }

    const sendAddCollapsedHeight = (height: number) =>
      parentPostMessage("add-collapsed-height", height);

    const newHeight = rootRef.current?.clientHeight
      ? rootRef.current.clientHeight + 25
      : 0;

    sendAddCollapsedHeight(newHeight);

    return () => sendAddCollapsedHeight(0);
  }, [
    parentPostMessage,
    widgetExpanded,
    isOverButton,
    contentRect,
    hidePopupMessages,
  ]);

  const popupMessagesStyle = {
    "--boxShadowXOffset": widgetPosition === "left" ? "-25px" : "25px",
    "--alignMessages": widgetPosition === "left" ? "flex-start" : "flex-end",
  } as React.CSSProperties;

  if (hidePopupMessages) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={classNames(
        widgetExpanded || !starterQuestions.length ? "pb-0" : "pb-3",
        !widgetExpanded && "w-[280px]",
      )}
      style={popupMessagesStyle}
    >
      {!widgetExpanded &&
        botInfo.bot_info_config.show_floating_welcome_message && (
          <div
            className="welcome-message cursor-pointer"
            onClick={() => setWidgetExpanded(true)}
          >
            <div className="company-panel">
              <img
                className="company-logo"
                src={botInfo.bot_info_config.company_logo}
              />
              <p className="grow overflow-auto break-words">
                {botInfo.bot_info_config.company_name}
              </p>
              {setPopupClosedByUser && (
                <button
                  className="close-button"
                  onClick={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    parentPostMessage("add-collapsed-height", 0);
                    setPopupClosedByUser(true);
                  }}
                >
                  <IconClose />
                </button>
              )}
            </div>
            <p>{botInfo.bot_info_config.welcome_message}</p>
          </div>
        )}
      {(botInfo.bot_info_config.show_starter_widget ||
        (widgetExpanded && !isOverButton)) && (
        <div
          className="popup-messages-container"
          style={{
            flexDirection: widgetExpanded ? "row" : "column",
          }}
        >
          {starterQuestions.map(starterQuestion => (
            <StarterQuestionButton
              key={starterQuestion.id}
              starterQuestion={starterQuestion}
              onSuggestionClick={onSuggestionClick}
              isOverButton={isOverButton}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PopupMessagesNonIframe;
